const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface NotificationTemplate {
  id: string;
  name: string;
  type: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  title: string;
  body: string;
  variables: Array<{
    name: string;
    description: string;
    type: 'string' | 'number' | 'date' | 'boolean';
    required: boolean;
    defaultValue: string;
  }>;
  data: Record<string, any>;
  isActive: boolean;
  createdBy: {
    id: string;
    nickname: string;
    email: string;
  };
  usageCount: number;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateData {
  name: string;
  type: 'analysis_complete' | 'system' | 'promotion' | 'marketing';
  title: string;
  body: string;
  variables?: Array<{
    name: string;
    description: string;
    type: 'string' | 'number' | 'date' | 'boolean';
    required: boolean;
    defaultValue: string;
  }>;
  data?: Record<string, any>;
}

export interface UpdateTemplateData extends Partial<CreateTemplateData> {
  isActive?: boolean;
}

export interface TemplateFilters {
  page?: number;
  limit?: number;
  type?: string;
  isActive?: boolean;
}

export interface CreateNotificationFromTemplateData {
  variables?: Record<string, any>;
  targetUsers: {
    type: 'all' | 'specific' | 'segment';
    userIds?: string[];
    segment?: 'premium' | 'free' | 'active' | 'inactive';
  };
  scheduledAt?: string;
}

class NotificationTemplateService {
  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // 템플릿 목록 조회
  async getTemplates(filters: TemplateFilters = {}): Promise<{
    templates: NotificationTemplate[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.type) params.append('type', filters.type);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());

    const response = await fetch(`${API_BASE_URL}/notification-templates?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }

    const data = await response.json();
    return data.data;
  }

  // 템플릿 상세 조회
  async getTemplate(id: string): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }

    const data = await response.json();
    return data.data;
  }

  // 템플릿 생성
  async createTemplate(data: CreateTemplateData): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/notification-templates`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create template');
    }

    const result = await response.json();
    return result.data;
  }

  // 템플릿 수정
  async updateTemplate(id: string, data: UpdateTemplateData): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update template');
    }

    const result = await response.json();
    return result.data;
  }

  // 템플릿 삭제
  async deleteTemplate(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete template');
    }
  }

  // 템플릿에서 알림 생성
  async createNotificationFromTemplate(id: string, data: CreateNotificationFromTemplateData): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}/create-notification`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create notification from template');
    }

    const result = await response.json();
    return result.data;
  }

  // 템플릿 미리보기
  async previewTemplate(
    id: string,
    variables: Record<string, any> = {}
  ): Promise<{
    title: string;
    body: string;
    type: string;
    data: Record<string, any>;
  }> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}/preview`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ variables }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to preview template');
    }

    const result = await response.json();
    return result.data;
  }

  // 템플릿 복제
  async duplicateTemplate(id: string, name: string): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/${id}/duplicate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to duplicate template');
    }

    const result = await response.json();
    return result.data;
  }

  // 기본 템플릿 생성
  async createDefaultTemplates(): Promise<NotificationTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/notification-templates/create-defaults`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create default templates');
    }

    const result = await response.json();
    return result.data;
  }
}

export const notificationTemplateService = new NotificationTemplateService();
